import React, { useState, useMemo, useCallback } from 'react';
import {
  Search,
  Filter,
  ChevronDown,
  MessageSquare,
  Phone,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { Ticket, TicketStatus, TicketPriority, TicketCategory } from '../../types/support';
import {
  cn,
  formatRelativeTime,
  getStatusLabel,
  getStatusBadgeClass,
  getPriorityLabel,
  getPriorityBadgeClass,
  getCategoryLabel,
  getUserDisplayName,
  debounce,
} from '../../utils/helpers';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import EmptyState from '../ui/EmptyState';

interface SupportTicketListProps {
  tickets: Ticket[];
  loading: boolean;
  selectedTicketId: string | null;
  onSelectTicket: (ticketId: string) => void;
  onFilterChange: (filters: TicketFilters) => void;
  filters: TicketFilters;
  total: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export interface TicketFilters {
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  search?: string;
}

const statusTabs = [
  { value: undefined, label: 'All' },
  { value: TicketStatus.OPEN, label: 'Open' },
  { value: TicketStatus.IN_PROGRESS, label: 'In Progress' },
  { value: TicketStatus.WAITING_FOR_CUSTOMER, label: 'Waiting' },
  { value: TicketStatus.RESOLVED, label: 'Resolved' },
];

const priorityOptions = [
  { value: undefined, label: 'All Priorities' },
  { value: TicketPriority.URGENT, label: 'Urgent' },
  { value: TicketPriority.HIGH, label: 'High' },
  { value: TicketPriority.MEDIUM, label: 'Medium' },
  { value: TicketPriority.LOW, label: 'Low' },
];

const categoryOptions = [
  { value: undefined, label: 'All Categories' },
  { value: TicketCategory.BOOKING, label: 'Booking' },
  { value: TicketCategory.PAYMENT, label: 'Payment' },
  { value: TicketCategory.REFUND, label: 'Refund' },
  { value: TicketCategory.ACCOUNT, label: 'Account' },
  { value: TicketCategory.TECHNICAL, label: 'Technical' },
  { value: TicketCategory.GENERAL, label: 'General' },
];

const SupportTicketList: React.FC<SupportTicketListProps> = ({
  tickets,
  loading,
  selectedTicketId,
  onSelectTicket,
  onFilterChange,
  filters,
  total,
  page,
  totalPages,
  onPageChange,
}) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChange({ ...filters, search: value || undefined });
      }, 300),
    [filters, onFilterChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleStatusChange = (status?: TicketStatus) => {
    onFilterChange({ ...filters, status });
  };

  const handlePriorityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFilterChange({
      ...filters,
      priority: value ? (value as TicketPriority) : undefined,
    });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFilterChange({
      ...filters,
      category: value ? (value as TicketCategory) : undefined,
    });
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Tickets
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({total})
            </span>
          </h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'p-2 rounded-lg transition-colors',
              showFilters
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:bg-primary/10'
            )}
          >
            <Filter className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchValue}
            onChange={handleSearchChange}
            className="input pl-10"
          />
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 overflow-x-auto scrollbar-thin pb-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.label}
              onClick={() => handleStatusChange(tab.value)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors',
                filters.status === tab.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Extra filters */}
        {showFilters && (
          <div className="flex gap-2 animate-fade-in">
            <select
              value={filters.priority || ''}
              onChange={handlePriorityChange}
              className="select text-sm flex-1"
            >
              {priorityOptions.map((opt) => (
                <option key={opt.label} value={opt.value || ''}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={filters.category || ''}
              onChange={handleCategoryChange}
              className="select text-sm flex-1"
            >
              {categoryOptions.map((opt) => (
                <option key={opt.label} value={opt.value || ''}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Ticket list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState
            title="No tickets found"
            description={
              filters.search || filters.status || filters.priority
                ? 'Try adjusting your filters'
                : 'No support tickets yet'
            }
          />
        ) : (
          <div className="divide-y divide-border">
            {tickets.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                isSelected={ticket.id === selectedTicketId}
                onClick={() => onSelectTicket(ticket.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="btn btn-ghost px-2 py-1 text-xs disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="btn btn-ghost px-2 py-1 text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual ticket row
interface TicketRowProps {
  ticket: Ticket;
  isSelected: boolean;
  onClick: () => void;
}

const TicketRow: React.FC<TicketRowProps> = ({ ticket, isSelected, onClick }) => {
  const isEscalated = ticket.escalationStatus !== 'NONE';
  const isUrgent = ticket.priority === TicketPriority.URGENT;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-4 transition-colors hover:bg-primary/5',
        isSelected && 'bg-primary/10 border-l-2 border-l-primary',
        isUrgent && !isSelected && 'bg-destructive/5'
      )}
    >
      <div className="flex items-start gap-3">
        <Avatar
          name={getUserDisplayName(ticket.user, ticket.userId)}
          src={ticket.user?.profileImage}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-medium text-foreground truncate flex-1">
              {ticket.title}
            </h3>
            {isEscalated && (
              <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
            )}
          </div>

          {/* User info */}
          <p className="text-xs text-muted-foreground mb-2 truncate">
            {getUserDisplayName(ticket.user, ticket.userId)}
            {ticket.user?.userType && (
              <span className="ml-1 text-primary/70">
                ({ticket.user.userType === 'PARENT' ? 'Parent' : 'Provider'})
              </span>
            )}
          </p>

          {/* Badges row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                'badge text-[10px]',
                getStatusBadgeClass(ticket.status)
              )}
            >
              {getStatusLabel(ticket.status)}
            </span>
            <span
              className={cn(
                'badge text-[10px]',
                getPriorityBadgeClass(ticket.priority)
              )}
            >
              {getPriorityLabel(ticket.priority)}
            </span>
            <span className="badge text-[10px] bg-secondary-700/50 text-secondary-300 border-secondary-600">
              {getCategoryLabel(ticket.category)}
            </span>
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatRelativeTime(ticket.lastActivityAt || ticket.updatedAt)}
            </span>
            {ticket._count && (
              <>
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {ticket._count.messages}
                </span>
                {ticket._count.calls > 0 && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {ticket._count.calls}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default SupportTicketList;
